import { defineSseEvent } from '@terra/shared-sse-contracts';
import {
  InternalNetworkOperationStatusSchema,
  InternalNetworkOperationTaskStatusSchema,
  ScenarioStatusSchema,
  TargetTypeSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import { InternalNetworkV2AchievementsSchema } from './internal-network-attack-path.schema';

export type InternalNetworkSseEventType =
  | 'DISCOVERY_STARTED'
  | 'DISCOVERY_STAGE_CHANGED'
  | 'DISCOVERY_COMPLETED'
  | 'DISCOVERY_FAILED'
  | 'SCENARIO_STATUS_CHANGED'
  | 'OPERATION_STATUS_CHANGED'
  | 'ENROLLMENT_HEARTBEAT'
  | 'ATTACK_PATH_STEP_RECORDED'
  | 'CLEANUP_ACTION_RECORDED'
  | 'ACHIEVEMENTS_UPDATED';

const SseBasePayloadSchema = z.object({
  networkId: z.string(),
  targetType: TargetTypeSchema,
  eventType: z.string(),
  eventSeq: z.number(),
});

export const DiscoveryStageChangedPayloadSchema = SseBasePayloadSchema.extend({
  discoveryRunId: z.string(),
  stage: z.string(),
  status: z.string(),
  remoteTaskId: z.number().optional(),
  entitiesImported: z.number().optional(),
  relationshipsImported: z.number().optional(),
  entitiesCollected: z.number().optional(),
  relationshipsCollected: z.number().optional(),
  entitiesNormalized: z.number().optional(),
  relationshipsNormalized: z.number().optional(),
  signalsGenerated: z.number().optional(),
  scenariosGenerated: z.number().optional(),
  processedSoFar: z.number().optional(),
  totalCount: z.number().optional(),
  message: z.string().optional(),
});

export type DiscoveryStageChangedPayload = z.infer<
  typeof DiscoveryStageChangedPayloadSchema
>;

export const ScenarioStatusChangedPayloadSchema = SseBasePayloadSchema.extend({
  scenarioId: z.string(),
  signalId: z.string(),
  status: ScenarioStatusSchema,
  executionId: z.string().optional(),
});

export type ScenarioStatusChangedPayload = z.infer<
  typeof ScenarioStatusChangedPayloadSchema
>;

export const OperationStatusChangedPayloadSchema = SseBasePayloadSchema.extend({
  operationId: z.string(),
  operationTaskId: z.string().optional(),
  scenarioId: z.string().nullable().optional(),
  signalId: z.string().nullable().optional(),
  status: InternalNetworkOperationStatusSchema,
  taskStatus: InternalNetworkOperationTaskStatusSchema.optional(),
  command: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
});

export type OperationStatusChangedPayload = z.infer<
  typeof OperationStatusChangedPayloadSchema
>;

export const EnrollmentHeartbeatPayloadSchema = SseBasePayloadSchema.extend({
  enrollmentId: z.string(),
  deploymentStatus: z.enum(['PENDING', 'ONLINE', 'OFFLINE', 'REVOKED']),
  version: z.string().nullable(),
  imageDigest: z.string().nullable(),
  lastHeartbeatAt: z.iso.datetime(),
});

export type EnrollmentHeartbeatPayload = z.infer<
  typeof EnrollmentHeartbeatPayloadSchema
>;

export const AttackPathStepRecordedPayloadSchema = SseBasePayloadSchema.extend({
  assessmentId: z.string().optional(),
  eventId: z.string(),
  eventKind: z.string(),
  exploitsWeakness: z.boolean(),
});

export type AttackPathStepRecordedPayload = z.infer<
  typeof AttackPathStepRecordedPayloadSchema
>;

export const CleanupActionRecordedPayloadSchema = SseBasePayloadSchema.extend({
  assessmentId: z.string(),
  actionId: z.string(),
  command: z.string(),
  status: z.enum(['SUCCEEDED', 'FAILED']),
  operationId: z.string().nullable(),
  remoteTaskId: z.number().nullable(),
  evidenceId: z.string().nullable(),
  summary: z.string().nullable(),
  failureReason: z.string().nullable(),
  recordedAt: z.iso.datetime(),
});

export type CleanupActionRecordedPayload = z.infer<
  typeof CleanupActionRecordedPayloadSchema
>;

export const AchievementsUpdatedPayloadSchema = SseBasePayloadSchema.extend({
  assessmentId: z.string(),
  phase: z.string(),
  completed: z.boolean(),
  achievements: InternalNetworkV2AchievementsSchema,
});

export type AchievementsUpdatedPayload = z.infer<
  typeof AchievementsUpdatedPayloadSchema
>;

const buildInternalNetworkEvent = (type: InternalNetworkSseEventType) =>
  defineSseEvent({
    type,
    payload: DiscoveryStageChangedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_DISCOVERY_STARTED_SSE_EVENT =
  buildInternalNetworkEvent('DISCOVERY_STARTED');

export const INTERNAL_NETWORK_DISCOVERY_STAGE_CHANGED_SSE_EVENT =
  defineSseEvent({
    type: 'DISCOVERY_STAGE_CHANGED' as const,
    payload: DiscoveryStageChangedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_DISCOVERY_COMPLETED_SSE_EVENT =
  buildInternalNetworkEvent('DISCOVERY_COMPLETED');

export const INTERNAL_NETWORK_DISCOVERY_FAILED_SSE_EVENT =
  buildInternalNetworkEvent('DISCOVERY_FAILED');

export const INTERNAL_NETWORK_SCENARIO_STATUS_CHANGED_SSE_EVENT =
  defineSseEvent({
    type: 'SCENARIO_STATUS_CHANGED' as const,
    payload: ScenarioStatusChangedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_OPERATION_STATUS_CHANGED_SSE_EVENT =
  defineSseEvent({
    type: 'OPERATION_STATUS_CHANGED' as const,
    payload: OperationStatusChangedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_ENROLLMENT_HEARTBEAT_SSE_EVENT = defineSseEvent({
  type: 'ENROLLMENT_HEARTBEAT' as const,
  payload: EnrollmentHeartbeatPayloadSchema,
  channelPrefix: 'internal-network',
  channelKey: ({ networkId, eventType, eventSeq }) =>
    `${networkId}-${eventType}-${eventSeq}`,
  target: {
    type: ({ targetType }) => targetType,
    id: ({ networkId }) => networkId,
  },
});

export const INTERNAL_NETWORK_ATTACK_PATH_STEP_RECORDED_SSE_EVENT =
  defineSseEvent({
    type: 'ATTACK_PATH_STEP_RECORDED' as const,
    payload: AttackPathStepRecordedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_CLEANUP_ACTION_RECORDED_SSE_EVENT =
  defineSseEvent({
    type: 'CLEANUP_ACTION_RECORDED' as const,
    payload: CleanupActionRecordedPayloadSchema,
    channelPrefix: 'internal-network',
    channelKey: ({ networkId, eventType, eventSeq }) =>
      `${networkId}-${eventType}-${eventSeq}`,
    target: {
      type: ({ targetType }) => targetType,
      id: ({ networkId }) => networkId,
    },
  });

export const INTERNAL_NETWORK_ACHIEVEMENTS_UPDATED_SSE_EVENT = defineSseEvent({
  type: 'ACHIEVEMENTS_UPDATED' as const,
  payload: AchievementsUpdatedPayloadSchema,
  channelPrefix: 'internal-network',
  channelKey: ({ networkId, eventType, eventSeq }) =>
    `${networkId}-${eventType}-${eventSeq}`,
  target: {
    type: ({ targetType }) => targetType,
    id: ({ networkId }) => networkId,
  },
});

export const INTERNAL_NETWORK_SSE_EVENTS = {
  DISCOVERY_STARTED: INTERNAL_NETWORK_DISCOVERY_STARTED_SSE_EVENT,
  DISCOVERY_STAGE_CHANGED: INTERNAL_NETWORK_DISCOVERY_STAGE_CHANGED_SSE_EVENT,
  DISCOVERY_COMPLETED: INTERNAL_NETWORK_DISCOVERY_COMPLETED_SSE_EVENT,
  DISCOVERY_FAILED: INTERNAL_NETWORK_DISCOVERY_FAILED_SSE_EVENT,
  SCENARIO_STATUS_CHANGED: INTERNAL_NETWORK_SCENARIO_STATUS_CHANGED_SSE_EVENT,
  OPERATION_STATUS_CHANGED: INTERNAL_NETWORK_OPERATION_STATUS_CHANGED_SSE_EVENT,
  ENROLLMENT_HEARTBEAT: INTERNAL_NETWORK_ENROLLMENT_HEARTBEAT_SSE_EVENT,
  ATTACK_PATH_STEP_RECORDED:
    INTERNAL_NETWORK_ATTACK_PATH_STEP_RECORDED_SSE_EVENT,
  CLEANUP_ACTION_RECORDED: INTERNAL_NETWORK_CLEANUP_ACTION_RECORDED_SSE_EVENT,
  ACHIEVEMENTS_UPDATED: INTERNAL_NETWORK_ACHIEVEMENTS_UPDATED_SSE_EVENT,
} as const;
